import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { VerticalAppliedFilters } from '@plato/ui-legacy/components/filters/vertical-applied-filters';
import { VerticalFilters } from '@plato/ui-legacy/components/filters/vertical-filters';
import { ErrorBadge } from '@plato/ui-legacy/components/ui/error-badge';
import Text from '@plato/ui-legacy/components/ui/text';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import { ActivityFeedModelType } from '../../../server/types/customer-activity-feed-types.ts';
import { cleanEmpty } from '../../../utils/clean-empty.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getUsedFilterEventPayload } from '../../analytics/utils/filter-analytics.ts';
import { useActivityFeed } from '../hooks/use-activity-feed.ts';
import {
    useActivityFilters,
    useCustomerStatusFilter,
    useInsightOutcomeFilter,
    useNoteTypeFilter,
    useOrderStatusFilter,
    useQuoteStatusFilter,
    useTaskStatusFilter,
} from '../hooks/use-activity-filters.ts';
import { useActivityOpenTasks } from '../hooks/use-activity-open-tasks.ts';
import { useActivityPinnedNotes } from '../hooks/use-activity-pinned-notes.ts';
import { useActivityUpcomingEvents } from '../hooks/use-activity-upcoming-events.ts';
import { ActivityEmptyState } from './activity-empty-state.tsx';
import { ActivityFeedTimeline } from './activity-feed-timeline.tsx';
import { ActivityModelTypeSelect } from './activity-model-type-select.tsx';
import { ActivityPinnedNotes } from './activity-pinned-notes.tsx';
import { ActivityUpcomingTimeline } from './activity-upcoming-timeline.tsx';

const MODEL_TYPE_OPTIONS = [
    ActivityFeedModelType.QUOTE,
    ActivityFeedModelType.ORDER,
    ActivityFeedModelType.NOTE,
    ActivityFeedModelType.TASK,
    ActivityFeedModelType.INSIGHT_OUTCOME,
    ActivityFeedModelType.CUSTOMER_STATUS_CHANGE,
];

export function CustomerActivityFeed({ customerBasics }: { customerBasics: CustomerDTO }): React.JSX.Element {
    const { t } = useTranslation('customer-detail');
    const { defaultCurrency } = useAppConfig();
    const customerId = customerBasics.customerId;
    const sendEvent = useAnalytics();

    const { defaultFilters, appliedFilters, setAppliedFilters } = useActivityFilters();

    const applyFilters = (values: typeof appliedFilters): void => {
        const next = cleanEmpty(values);
        const filterPayload = getUsedFilterEventPayload(next);
        if (filterPayload.filters.length > 0) {
            sendEvent('customer_activity_feed_filter_used', filterPayload);
        }
        setAppliedFilters(next);
    };

    const filters = [
        useQuoteStatusFilter(),
        useOrderStatusFilter(),
        useTaskStatusFilter(),
        useNoteTypeFilter(),
        useInsightOutcomeFilter(),
        useCustomerStatusFilter(),
    ];

    const {
        notes: pinnedNotes,
        isLoading: isLoadingPinnedNotes,
        error: pinnedNotesError,
    } = useActivityPinnedNotes(customerId);

    const {
        taskData: openTasks,
        isLoading: isLoadingOpenTasks,
        error: openTasksError,
    } = useActivityOpenTasks(customerId);

    const {
        events: upcomingEvents,
        isLoading: isLoadingUpcomingEvents,
        error: upcomingEventsError,
    } = useActivityUpcomingEvents(customerId);

    const {
        isLoading: isLoadingTimeline,
        fetchNextPage,
        hasNextPage,
        timeGroupedData,
        isFetchingNextPage,
        error: timelineError,
    } = useActivityFeed({ customerId, modelTypes: MODEL_TYPE_OPTIONS });

    const error = pinnedNotesError || openTasksError || upcomingEventsError || timelineError;
    const isLoading =
        isLoadingPinnedNotes || isLoadingOpenTasks || isLoadingUpcomingEvents || isLoadingTimeline;
    const isEmpty =
        pinnedNotes.length + openTasks.length + upcomingEvents.length + timeGroupedData.size === 0;

    const modelTypeSelect = (
        <ActivityModelTypeSelect
            value={appliedFilters.modelType}
            onChange={(modelType) => {
                setAppliedFilters({ ...appliedFilters, modelType });
            }}
            options={MODEL_TYPE_OPTIONS}
        />
    );

    return (
        <section className="flex flex-1 flex-col gap-3">
            <div className="sticky top-0 z-10 -my-3 flex w-full flex-col gap-3 bg-white py-3">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <Text variant="h1" className="shrink-0">
                        {t('customer_detail__timeline')}
                    </Text>
                    <VerticalFilters
                        defaultCurrency={defaultCurrency}
                        filters={filters}
                        isLoading={false}
                        searchFilterKey={'searchText'}
                        searchPlaceholder={t('search_placeholder__customer', { ns: 'activity-feed' })}
                        defaultFilters={defaultFilters}
                        appliedFilters={appliedFilters}
                        onApplyFilters={applyFilters}
                        rightSlot={modelTypeSelect}
                    />
                </div>
                <VerticalAppliedFilters
                    defaultCurrency={defaultCurrency}
                    filters={filters}
                    defaultFilters={defaultFilters}
                    appliedFilters={appliedFilters}
                    onApplyFilters={applyFilters}
                />
            </div>
            <ActivityPinnedNotes notes={pinnedNotes} isLoading={isLoadingPinnedNotes} />
            <ActivityUpcomingTimeline
                tasks={openTasks}
                events={upcomingEvents}
                isLoading={isLoadingUpcomingEvents || isLoadingOpenTasks}
            />
            {timeGroupedData.size > 0 && (
                <Accordion.Root type="multiple" defaultValue={['past-activities']}>
                    <Accordion.Item value="past-activities">
                        <Accordion.Trigger className="flex items-center group">
                            <div className="flex h-6 w-6 items-center justify-center ml-1 mr-2">
                                <ChevronDownIcon className="w-4 h-4 text-neutral-700 group-data-[state=open]:rotate-180" />
                            </div>
                            <Text variant="h1">{t('customer_detail__past_activities')}</Text>
                        </Accordion.Trigger>
                        <Accordion.Content className="pt-3">
                            <ActivityFeedTimeline
                                isLoading={isLoadingTimeline}
                                fetchNextPage={fetchNextPage}
                                hasNextPage={hasNextPage}
                                timeGroupedData={timeGroupedData}
                                isFetchingNextPage={isFetchingNextPage}
                                source="customer_detail"
                            />
                        </Accordion.Content>
                    </Accordion.Item>
                </Accordion.Root>
            )}
            {isEmpty && !isLoading && <ActivityEmptyState customer={customerBasics} />}
            {!isFetchingNextPage && error?.message && <ErrorBadge>{error.message}</ErrorBadge>}
        </section>
    );
}
